import { selectLeaseComps } from "@/features/survey/leaseCompsSelectors";
import {
  removeLeaseComp,
  setCurrentLeaseCompId,
  setLeaseComps,
} from "@/features/survey/leaseCompsSlice";
import {
  useDeleteLeaseCompMutation,
  useUpdateLeaseCompVisibilityMutation,
} from "@/services/leaseComps";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import Switch from "@components/Switch";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldIconButton } from "@emerald-react/icon-button";
import useSimpleConfirmation from "@hooks/useSimpleConfirmation";
import { useSortableItem } from "@hooks/useSortable";
import useSurveyState from "@hooks/useSurveyState";
import Analytics from "@shared/utils/tagManagerAnalytics";
import type { LeaseComp } from "@survey-types/survey.leaseComp.types";
import classNames from "classnames";

interface LeaseCompListItemProps {
  leaseComp: LeaseComp;
  index: number;
  isSelected?: boolean;
  onToggleSelect?: (id: string, selected: boolean) => void;
}

const LeaseCompListItem = ({ leaseComp, index, isSelected = false, onToggleSelect }: LeaseCompListItemProps) => {
  const dispatch = useAppDispatch();
  const allLeaseComps = useAppSelector(selectLeaseComps);
  const { attributes, listeners, setNodeRef, style, isDragging } =
    useSortableItem(leaseComp.leaseCompId);
  const [deleteLeaseComp] = useDeleteLeaseCompMutation();
  const [updateVisibility] = useUpdateLeaseCompVisibilityMutation();
  const { quickDelete, withLoading } = useSimpleConfirmation();
  const { survey } = useSurveyState();

  const handleToggleVisibility = () => {
    updateVisibility({
      leaseCompId: leaseComp.leaseCompId,
      isHidden: !leaseComp.isHidden,
      surveyId: leaseComp.surveyId,
    });
  };

  const handleDelete = () => {
    quickDelete({
      title: "Lease Comp",
      text: `This will remove the Lease Comp and all of its information from the survey.\nAre you sure you want to delete this Lease Comp?`,
      id: "SC-ConfirmDeleteLeaseComp",
    })
      .then((confirmed) => {
        if (confirmed) {
          const previousLeaseComps = allLeaseComps;

          dispatch(removeLeaseComp(leaseComp.leaseCompId));

          withLoading(
            () =>
              deleteLeaseComp({
                leaseCompId: leaseComp.leaseCompId,
                surveyId: leaseComp.surveyId,
              }).unwrap(),
            {
              loadingText: "Deleting Lease Comp...",
              successText: "Lease Comp deleted successfully!",
              errorText: "Failed to delete Lease Comp.",
            },
          ).catch((_error) => {
            console.error("Failed to delete lease comp");
            dispatch(setLeaseComps(previousLeaseComps));
          });
        }
      })
      .catch((_error) => {
        console.error("Error in confirmation dialog");
      });
  };

  const handleSelectLeaseComp = () => {
    Analytics.send({
      event: "Edit lease comp",
      leaseCompId: leaseComp.leaseCompId,
      SurveyID: survey?.id,
    });
    dispatch(setCurrentLeaseCompId({ leaseCompId: leaseComp.leaseCompId }));
  };

  return (
    <div
      id="SC-DragHandleLeaseComp"
      ref={setNodeRef}
      style={style}
      className={classNames(
        "flex items-center gap-4 p-4 bg-primary-lighter hover:bg-divider-2 transition-colors group",
        {
          "opacity-50 bg-slate-100": isDragging,
        },
      )}
    >
      {/* Drag Handle */}
      <div
        className="flex items-center text-slate-400 cursor-move"
        {...attributes}
        {...listeners}
      >
        <EmeraldIcon
          icon="drag_indicator"
          className="text-gray-400 hover:text-gray-600 transition-colors"
        />
      </div>

       {/* Checkbox — native input avoids MDCCheckbox stale foundation state */}
      {onToggleSelect && (
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onToggleSelect(leaseComp.leaseCompId, e.target.checked)}
          className="cursor-pointer"
          style={{ width: '16px', height: '16px', accentColor: '#003f2d', flexShrink: 0 }}
        />
      )}

      {/* Order Number */}
      <div
        className="w-6 text-sm font-medium text-primary flex items-center justify-center"
        style={{
          border: "1px solid white",
          borderColor: leaseComp.isHidden ? "transparent" : "white",
          backgroundColor: leaseComp.isHidden
            ? "transparent"
            : leaseComp.mapData?.iconColor || "#E47444",
          color: leaseComp.isHidden ? "#003f2d" : "white",
          padding: "2px",
        }}
      >
        {leaseComp.isHidden ? "–" : index + 1}
      </div>

      {/* Lease Comp Info */}
      <div
        className="flex-1 hover:cursor-pointer hover:underline flex gap-x-3"
        onClick={handleSelectLeaseComp}
      >
        <div className="flex-col flex">
          <div className="truncate text-base font-normal text-primary">
            {leaseComp.name}
          </div>
          <div className="text-sm text-slate-600">
            {leaseComp.leaseCompFields?.propertyAddress &&
              `${leaseComp.leaseCompFields.propertyAddress}, `}
            {leaseComp.leaseCompFields?.propertyCity}
            {leaseComp.leaseCompFields?.propertyState &&
              `, ${leaseComp.leaseCompFields.propertyState}`}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex w-52 items-center gap-x-10">
        <Switch
          checked={!leaseComp.isHidden}
          onChange={handleToggleVisibility}
          size="small"
          label="Show"
          labelPosition="left"
        />

        {/* Delete Button */}
        <EmeraldIconButton
          icon="delete"
          type="Icon"
          className="text-data-light transition-all"
          onClick={handleDelete}
          id="SC-DeleteLeaseComp"
        />
      </div>
    </div>
  );
};

export default LeaseCompListItem;
